import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { LogOut, User, Shield, LayoutDashboard, ChevronDown, CreditCard } from "lucide-react";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuSeparator, DropdownMenuLabel,
} from "./ui/dropdown-menu";
import { useAuth } from "../context/AuthContext";
import UserAvatar from "./UserAvatar";
import WalletBadge from "./billing/WalletBadge";

/**
 * Header drop-down — replaces the bare sign-out icon. Always renders Account
 * + Sign out; adds Admin / Workspace links contextually.
 *
 * Props:
 *   variant: "workspace" | "admin" — controls which cross-link to show.
 */
export default function UserMenu({ variant = "workspace" }) {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  if (!user) return null;

  return (
    <div className="flex items-center gap-2">
      {user.role !== "super_admin" && <WalletBadge />}
      <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          className="flex items-center gap-1.5 px-1.5 py-1 rounded-sm hover:bg-secondary transition"
          data-testid="user-menu-trigger"
        >
          <UserAvatar user={user} size="sm" testId="user-menu-avatar" />
          <ChevronDown className="h-3 w-3 text-muted-foreground" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56 rounded-sm">
        <DropdownMenuLabel>
          <div className="flex items-center gap-2 min-w-0">
            <UserAvatar user={user} size="md" />
            <div className="min-w-0 flex-1">
              <div className="font-semibold text-sm truncate">{user.name || t("common:no_name", "(sin nombre)")}</div>
              <div className="text-[11px] font-mono text-muted-foreground truncate">{user.email}</div>
            </div>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild data-testid="user-menu-account">
          <Link to="/account" className="cursor-pointer">
            <User className="h-3.5 w-3.5 mr-2" /> {t("nav.account")}
          </Link>
        </DropdownMenuItem>
        {user.role !== "super_admin" && (
          <DropdownMenuItem asChild data-testid="user-menu-billing">
            <Link to="/billing" className="cursor-pointer">
              <CreditCard className="h-3.5 w-3.5 mr-2" /> {t("nav.billing")}
            </Link>
          </DropdownMenuItem>
        )}
        {variant === "workspace" && user.role === "super_admin" && (
          <DropdownMenuItem asChild data-testid="user-menu-admin">
            <Link to="/admin" className="cursor-pointer">
              <Shield className="h-3.5 w-3.5 mr-2" /> {t("nav.admin_panel", "Panel de administración")}
            </Link>
          </DropdownMenuItem>
        )}
        {variant === "admin" && (
          <DropdownMenuItem asChild data-testid="user-menu-workspace">
            <Link to="/workspace" className="cursor-pointer">
              <LayoutDashboard className="h-3.5 w-3.5 mr-2" /> {t("nav.workspace")}
            </Link>
          </DropdownMenuItem>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={async () => { await logout(); navigate("/login"); }}
          className="cursor-pointer text-destructive focus:text-destructive"
          data-testid="user-menu-logout"
        >
          <LogOut className="h-3.5 w-3.5 mr-2" /> {t("nav.logout")}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
    </div>
  );
}
